import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaAddressCard, FaEdit, FaEnvelope, FaPhone, FaSave, FaUserGraduate } from "react-icons/fa";
import http from "../../api/apiClient";
import { useCan } from "../../authz/useCan";
import DutchAddressFields from "../../components/DutchAddressFields";
import { LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import { toIsoDate } from "../../utils/dateUtils";

type TeacherDetailsModel = {
  firstName: string;
  lastName: string;
  address: string;
  zipCode: string;
  city: string;
  phone: string;
  email: string;
  dateBirth: string;
  sharePhone: boolean;
  shareEmail: boolean;
  shareAddress: boolean;
};

const emptyDetails: TeacherDetailsModel = {
  firstName: "", lastName: "", address: "", zipCode: "", city: "", phone: "", email: "", dateBirth: "",
  sharePhone: false, shareEmail: false, shareAddress: false,
};

const asText = (value: unknown) => value == null ? "" : typeof value === "string" ? value : String(value);

const normalizeTeacherDetails = (data: unknown): TeacherDetailsModel => {
  const source = data && typeof data === "object" ? data as Record<string, unknown> : {};
  return {
    firstName: asText(source.firstName),
    lastName: asText(source.lastName),
    address: asText(source.address),
    zipCode: asText(source.zipCode),
    city: asText(source.city),
    phone: asText(source.phone),
    email: asText(source.email),
    dateBirth: toIsoDate(source.dateBirth as string | number[] | null | undefined) || "",
    sharePhone: source.sharePhone === true,
    shareEmail: source.shareEmail === true,
    shareAddress: source.shareAddress === true,
  };
};

const inputClass = "mt-1 h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-950 shadow-sm outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-200 dark:border-white/10 dark:bg-slate-950/60 dark:text-white";
const labelClass = "flex min-w-0 flex-col text-xs font-semibold text-slate-500 dark:text-slate-300";
const sectionClass = "rounded-[24px] border border-slate-200/80 bg-white/95 p-5 shadow-sm dark:border-white/10 dark:bg-white/5 sm:p-6";

const TeacherDetails: React.FC = () => {
  const { t } = useTranslation();
  const canUpdate = useCan("my.teacher-details", "update");
  const [details, setDetails] = useState<TeacherDetailsModel>(emptyDetails);
  const [draft, setDraft] = useState<TeacherDetailsModel>(emptyDetails);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    http.get<TeacherDetailsModel>("/teachers/me")
      .then(({ data }) => {
        if (!active) return;
        const next = normalizeTeacherDetails(data);
        setDetails(next);
        setDraft(next);
      })
      .catch(() => active && setError(t("myEnvironment.teacherDetails.loadFailed", { defaultValue: "Your teacher details could not be loaded." })))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [t]);

  const setField = (field: keyof TeacherDetailsModel, value: string | boolean) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true); setError(""); setMessage("");
    try {
      const { data } = await http.put<TeacherDetailsModel>("/teachers/me", draft);
      const next = normalizeTeacherDetails(data);
      setDetails(next); setDraft(next); setEditing(false);
      setMessage(t("myEnvironment.teacherDetails.saved", { defaultValue: "Your details have been saved." }));
    } catch {
      setError(t("myEnvironment.teacherDetails.saveFailed", { defaultValue: "Your details could not be saved." }));
    } finally {
      setSaving(false);
    }
  };

  const value = (text: unknown) => asText(text).trim() || t("common.not_available", { defaultValue: "Not available" });

  return (
    <LdsOverviewPage>
      <LdsOverviewHero icon={FaUserGraduate} eyebrow={t("myEnvironment.eyebrow", { defaultValue: "Personal workspace" })} title={t("myEnvironment.teacherDetails.title", { defaultValue: "My details" })} description={t("myEnvironment.teacherDetails.description", { defaultValue: "View and manage your personal teacher details." })} />
      {message ? <p role="status" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 font-semibold text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200">{message}</p> : null}
      {error ? <p role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-4 font-semibold text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">{error}</p> : null}
      {loading ? <p className={sectionClass}>{t("common.loading", { defaultValue: "Loading..." })}</p> : (
        <form onSubmit={save} className="space-y-5">
          <section className={sectionClass}>
            <div className="mb-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3"><FaUserGraduate className="text-indigo-600" /><h2 className="text-lg font-bold text-slate-950 dark:text-white">{t("teachers.form.personal_details", { defaultValue: "Personal details" })}</h2></div>
              {!editing && canUpdate ? <button type="button" onClick={() => { setDraft(details); setEditing(true); setMessage(""); }} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white"><FaEdit />{t("common.actions.edit", { defaultValue: "Edit" })}</button> : null}
            </div>
            {editing ? <div className="grid gap-5 sm:grid-cols-2">
              <label className={labelClass}>{t("common.given_name", { defaultValue: "Given name" })}<input required className={inputClass} value={draft.firstName} onChange={(e) => setField("firstName", e.target.value)} /></label>
              <label className={labelClass}>{t("common.family_name", { defaultValue: "Family name" })}<input required className={inputClass} value={draft.lastName} onChange={(e) => setField("lastName", e.target.value)} /></label>
              <label className={labelClass}>{t("family.common.phone", { defaultValue: "Phone" })}<input className={inputClass} value={draft.phone} onChange={(e) => setField("phone", e.target.value)} /></label>
              <label className={labelClass}>{t("family.common.email", { defaultValue: "Email" })}<input type="email" className={inputClass} value={draft.email} onChange={(e) => setField("email", e.target.value)} /></label>
              <label className={labelClass}>{t("children.common.date_of_birth", { defaultValue: "Date of birth" })}<input type="date" className={inputClass} value={draft.dateBirth || ""} onChange={(e) => setField("dateBirth", e.target.value)} /></label>
            </div> : <dl className="grid gap-5 sm:grid-cols-2">
              <div><dt className="text-xs font-bold uppercase text-slate-500">{t("common.name", { defaultValue: "Name" })}</dt><dd className="mt-1 font-semibold dark:text-white">{value(`${details.firstName} ${details.lastName}`)}</dd></div>
              <div><dt className="text-xs font-bold uppercase text-slate-500">{t("family.common.email", { defaultValue: "Email" })}</dt><dd className="mt-1 font-semibold dark:text-white">{value(details.email)}</dd></div>
              <div><dt className="text-xs font-bold uppercase text-slate-500">{t("family.common.phone", { defaultValue: "Phone" })}</dt><dd className="mt-1 font-semibold dark:text-white">{value(details.phone)}</dd></div>
              <div><dt className="text-xs font-bold uppercase text-slate-500">{t("children.common.date_of_birth", { defaultValue: "Date of birth" })}</dt><dd className="mt-1 font-semibold dark:text-white">{value(details.dateBirth)}</dd></div>
            </dl>}
          </section>

          <section className={sectionClass}>
            <div className="mb-5 flex items-center gap-3"><FaAddressCard className="text-amber-600" /><h2 className="text-lg font-bold text-slate-950 dark:text-white">{t("teachers.form.address_details", { defaultValue: "Address details" })}</h2></div>
            {editing ? <div className="grid gap-5 sm:grid-cols-2"><DutchAddressFields address={draft.address} zipCode={draft.zipCode} city={draft.city} onChange={(field, next) => setField(field, next)} onAddressFound={(found) => setDraft((current) => ({ ...current, ...found }))} t={t} inputClassName={inputClass} labelClassName={labelClass} addressClassName="sm:col-span-2" /></div> : <div className="flex items-start gap-3 text-slate-700 dark:text-slate-200"><FaAddressCard className="mt-1" /><p>{value([details.address, [details.zipCode, details.city].filter(Boolean).join(" ")].filter(Boolean).join("\n"))}</p></div>}
          </section>

          <section className={sectionClass}>
            <h2 className="mb-4 text-lg font-bold text-slate-950 dark:text-white">{t("teachers.form.privacy_publication", { defaultValue: "Privacy and publication" })}</h2>
            <div className="grid gap-3 sm:grid-cols-3">{[["sharePhone", FaPhone, "Share phone number"], ["shareEmail", FaEnvelope, "Share email"], ["shareAddress", FaAddressCard, "Share address"]].map(([field, Icon, label]) => <label key={String(field)} className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4 dark:border-white/10"><input type="checkbox" disabled={!editing} checked={Boolean(draft[field as keyof TeacherDetailsModel])} onChange={(e) => setField(field as keyof TeacherDetailsModel, e.target.checked)} /><Icon className="text-indigo-500" /><span className="font-semibold dark:text-white">{t(`teachers.edit.${String(field).replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)}`, { defaultValue: label })}</span></label>)}</div>
          </section>

          {editing ? <div className="flex justify-end gap-3"><button type="button" onClick={() => { setDraft(details); setEditing(false); }} className="rounded-full border border-slate-200 px-5 py-2.5 font-bold dark:border-white/10 dark:text-white">{t("common.cancel", { defaultValue: "Cancel" })}</button><button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-2.5 font-bold text-white disabled:opacity-60"><FaSave />{saving ? t("common.saving", { defaultValue: "Saving..." }) : t("common.save", { defaultValue: "Save" })}</button></div> : null}
        </form>
      )}
    </LdsOverviewPage>
  );
};

export default TeacherDetails;
